// 위젯 A1 — 남양주 통합돌봄 12단계 워크플로우 시각화.
// 원천: 남양주시 통합돌봄과 「N-CARE 통합돌봄 플랫폼 구축 계획(안)」(2026-08-28) PDF 1쪽 도식.
//
// PDF 원문 도식:
//   [단계 1-3 발굴·조사] 1.신청 → 2.사전조사 → 3.AI진단1차 → 4.접수
//   [단계 5-8 판정·계획] 5.종합판정 → 6.긴급돌봄 → 7.개인별지원계획 → 8.통합지원회의
//   [단계 9-12 제공·관리] 9.서비스의뢰 → 10.서비스제공 → 11.모니터링 → 12.종결
//
// 시각 원칙: 스마트(오늘의 증감) · 고급(여백·타이포·미묘한 인터랙션) · 편리(원터치 케이스 목록).
// 미구현 단계(5·8·11)는 회색 카드 + 배포 예정 note. 김해(R018) 등에는 렌더되지 않음.
import * as React from "react";
import { ServiceFlowDrilldownModal } from "../../ServiceFlowDrilldownModal";

// 원 안의 숫자 글리프 — 위젯 카드에 단계 번호를 명확히 노출.
const CIRCLED = ["①","②","③","④","⑤","⑥","⑦","⑧","⑨","⑩","⑪","⑫"];

// PDF 색상 코드(파랑/초록/주황)에 대응. tcare 디자인 토큰 그대로 사용.
const GROUP_META = {
  discover: { label: "발굴·조사", color: "sky",    range: "1 – 4"  },
  plan:     { label: "판정·계획", color: "green",  range: "5 – 8"  },
  provide:  { label: "제공·관리", color: "orange", range: "9 – 12" },
} as const;

type GroupKey = keyof typeof GROUP_META;

interface Stage {
  step: number;
  key: string;
  group: GroupKey;
  label: string;
  total: number;
  today: number;
  implemented: boolean;
  note?: string | null;
  // 서버가 이 단계를 드릴다운할 수 있다고 표시했는지(미구현 단계는 false).
  drilldown?: boolean;
}

interface Props {
  data: {
    region: { name: string; code: string; area?: string };
    generated_at?: string;
    source?: string;
    stages: Stage[];
  };
}

export function ServiceFlow({ data }: Props) {
  const stages = data.stages;
  // 카드 클릭 → 그 단계를 이루는 실제 건 목록(드릴다운 모달).
  // 예전엔 /cases 같은 화면으로 이동만 해서 아무것도 필터되지 않았다.
  const [drilldown, setDrilldown] = React.useState<Stage | null>(null);
  const groups: GroupKey[] = ["discover", "plan", "provide"];
  const implCount = stages.filter((s) => s.implemented).length;

  // 상단 라인: 스마트 요약. 오늘 증감이 있는 단계만 골라 한 줄로 배지.
  const todayHighlights = stages
    .filter((s) => s.implemented && s.today > 0)
    .sort((a, b) => b.today - a.today)
    .slice(0, 3);

  // 그룹 라벨 클릭 시 해당 섹션으로 부드럽게 스크롤 — 4쌍 자원, 그룹별 배정 편의.
  // 각 그룹 wrapper에 id를 부여하고 여기서 scrollIntoView.
  const jumpToGroup = (g: GroupKey) => {
    const el = document.getElementById(`nyj-flow-${g}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="card" style={{
      overflow: "hidden",
      // 히어로 위젯 강조 — 그라디언트 상단 스트립 + 오렌지 액센트 테두리 + 부드러운 그림자.
      borderTop: "3px solid var(--orange-500)",
      boxShadow: "0 8px 28px -12px rgba(255, 122, 0, 0.28), 0 2px 6px rgba(0,0,0,0.04)",
      background: "linear-gradient(180deg, var(--orange-50) 0%, var(--surface) 88px)",
    }}>
      {/* 헤더 */}
      <div style={{ padding: "20px 22px 12px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28, borderRadius: 8,
              background: "var(--orange-500)", color: "white",
              fontSize: 15, fontWeight: 800,
            }}>①</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: "var(--ink-1)", letterSpacing: -0.3 }}>
              남양주 통합돌봄 12단계 흐름
            </span>
            <span className="badge badge-green" title="실시간 데이터" style={{ fontSize: 10, letterSpacing: 0.4, fontWeight: 800 }}>● LIVE</span>
            <span className="badge badge-orange" title="핵심 위젯" style={{ fontSize: 10, letterSpacing: 0.4, fontWeight: 800 }}>핵심 워크플로우</span>
          </div>
          <div className="muted" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.6, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
            <span>남양주시 통합돌봄과 「N-CARE 통합돌봄 플랫폼 구축 계획(안)」·</span>
            {groups.map((g, i) => (
              <React.Fragment key={g}>
                <button
                  type="button"
                  onClick={() => jumpToGroup(g)}
                  title={`${GROUP_META[g].label} 그룹으로 이동`}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: "2px 6px",
                    borderRadius: 6,
                    color: `var(--${GROUP_META[g].color}-600)`,
                    fontWeight: 700,
                    fontSize: 11,
                    cursor: "pointer",
                    textDecoration: "underline",
                    textDecorationStyle: "dotted",
                    textUnderlineOffset: 3,
                    font: "inherit",
                  }}
                >
                  {GROUP_META[g].label}
                </button>
                {i < groups.length - 1 && <span style={{ color: "var(--ink-4)" }}>›</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, whiteSpace: "nowrap" }}>
          <span className="muted" style={{ fontSize: 10.5 }}>
            구현 완료 <b style={{ color: "var(--ink-1)", fontWeight: 700 }}>{implCount}</b> / 12
          </span>
          {data.generated_at && (
            <span className="muted mono" style={{ fontSize: 10 }}>
              {new Date(data.generated_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })} 기준
            </span>
          )}
        </div>
      </div>

      {/* 오늘의 스마트 요약 라인 */}
      {todayHighlights.length > 0 && (
        <div style={{ margin: "0 22px 6px", padding: "8px 12px", background: "var(--bg-sub)", borderRadius: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 10.5, color: "var(--orange-600)", fontWeight: 800, letterSpacing: 0.4 }}>TODAY</span>
          {todayHighlights.map((s) => (
            <span key={s.key} style={{ fontSize: 11.5, color: "var(--ink-2)" }}>
              <b style={{ color: `var(--${GROUP_META[s.group].color}-600)`, fontWeight: 700 }}>{s.label}</b>{" "}
              <span className="mono" style={{ fontWeight: 700 }}>+{s.today}</span>
            </span>
          ))}
        </div>
      )}

      {/* 3그룹 × 4단계 렌더 — 각 그룹에 id 부여로 서브타이틀 앵커 이동 지원 */}
      <div style={{ padding: "12px 22px 22px" }}>
        {groups.map((g, gi) => {
          const rowStages = stages.filter((s) => s.group === g).sort((a, b) => a.step - b.step);
          const meta = GROUP_META[g];
          return (
            <div key={g} id={`nyj-flow-${g}`} style={{
              marginBottom: gi < groups.length - 1 ? 16 : 0,
              scrollMarginTop: 12,
            }}>
              <div style={{
                display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10, paddingLeft: 2,
                paddingBottom: 6, borderBottom: `2px solid var(--${meta.color}-500)`,
              }}>
                <span style={{
                  fontSize: 12, fontWeight: 800, color: `var(--${meta.color}-600)`,
                  letterSpacing: 0.5,
                  padding: "2px 8px", background: `var(--${meta.color}-50)`, borderRadius: 6,
                }}>
                  {meta.label}
                </span>
                <span className="muted mono" style={{ fontSize: 10.5 }}>단계 {meta.range}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, alignItems: "stretch" }}>
                {rowStages.map((s) => (
                  <StageCard key={s.key} stage={s} colorToken={meta.color}
                    onOpen={() => setDrilldown(s)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {drilldown && (
        <ServiceFlowDrilldownModal stage={drilldown} regionCode={data.region?.code}
          onClose={() => setDrilldown(null)} />
      )}
    </div>
  );
}

function StageCard({ stage, colorToken, onOpen }: { stage: Stage; colorToken: string; onOpen: () => void }) {
  const impl = stage.implemented;
  const [hover, setHover] = React.useState(false);
  const accent = `var(--${colorToken}-500)`;
  const accentDeep = `var(--${colorToken}-600)`;
  const tint = `var(--${colorToken}-50)`;

  // 구현된 단계만 클릭 가능 — 클릭하면 그 단계에 실제로 속한 건 목록이 열린다.
  // (미구현 5·8·11 단계는 데이터 자체가 없으므로 비활성)
  const clickable = impl && stage.drilldown !== false;

  const handleClick = () => {
    if (clickable) onOpen();
  };
  const handleKey = (e: React.KeyboardEvent) => {
    if (clickable && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      title={
        clickable   ? `${stage.label} · 총 ${stage.total}건 · 클릭 시 해당 건 목록 보기`
        : impl      ? `${stage.label} · 총 ${stage.total}건`
        : (stage.note || "구현 예정")
      }
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={clickable ? handleClick : undefined}
      onKeyDown={handleKey}
      style={{
        background: impl ? "var(--surface)" : "var(--bg-sub)",
        border: "1px solid var(--border-soft)",
        borderRadius: 12,
        padding: "12px 12px 10px",
        cursor: clickable ? "pointer" : "default",
        opacity: impl ? 1 : 0.72,
        transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
        transform: clickable && hover ? "translateY(-2px)" : "none",
        boxShadow: clickable && hover ? `0 6px 16px -6px ${accent}, 0 2px 4px rgba(0,0,0,0.04)` : "none",
        borderColor: clickable && hover ? accent : "var(--border-soft)",
        display: "flex", flexDirection: "column", minHeight: 96,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            fontSize: 15,
            color: impl ? accentDeep : "var(--ink-4)",
            fontWeight: 800,
            width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: impl ? tint : "transparent", borderRadius: 6, flexShrink: 0,
          }}
        >
          {CIRCLED[stage.step - 1]}
        </span>
        <span
          style={{
            fontSize: 11.5, color: "var(--ink-2)", fontWeight: 700,
            flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {stage.label}
        </span>
      </div>
      <div
        style={{
          fontSize: 26, fontWeight: 800, color: impl ? accentDeep : "var(--ink-4)",
          fontVariantNumeric: "tabular-nums", marginTop: 6, lineHeight: 1,
        }}
      >
        {impl ? stage.total.toLocaleString("ko-KR") : "—"}
      </div>
      <div style={{ fontSize: 10.5, color: "var(--ink-3)", marginTop: "auto", paddingTop: 6, minHeight: 14 }}>
        {impl ? (
          stage.today > 0 ? (
            <span>
              오늘 <b style={{ color: accentDeep, fontWeight: 700 }}>+{stage.today}</b>
            </span>
          ) : (
            <span className="muted">오늘 신규 없음</span>
          )
        ) : (
          <span style={{ color: "var(--ink-4)", fontWeight: 600 }}>{stage.note}</span>
        )}
      </div>
      <div
        style={{
          height: 3, background: impl ? accent : "var(--ink-4)",
          opacity: impl ? Math.min(0.9, 0.25 + Math.log10(stage.total + 1) * 0.25) : 0.15,
          marginTop: 8, borderRadius: 99,
        }}
      />
    </div>
  );
}
